import React, { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";
import api, { formatApiError } from "../lib/api";

const initial = {
  name: "", business: "", email: "", phone: "", current_website: "",
  industry: "", website_type: "", budget: "", message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, lead_source: "contact" });
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <Seo page="contact" />
      <Container className="grid lg:grid-cols-5 gap-12 pb-24">
        <Reveal className="lg:col-span-2">
          <SectionLabel>Contact</SectionLabel>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F3EDDC]">Let's build something great</h1>
          <p className="mt-5 text-lg text-[#B7AF98]">Tell us about your project and we'll get back to you with a free quote.</p>
          <div className="mt-10 space-y-5">
            <a href="mailto:hello@weblaunchstudio.com" className="flex items-center gap-4 text-[#D2CAB2] hover:text-[#C9A227]">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#C9A227]/10 text-[#C9A227]"><Mail className="h-5 w-5" /></span>
              hello@weblaunchstudio.com
            </a>
          </div>
        </Reveal>

        <Reveal delay={120} className="lg:col-span-3">
          <div className="rounded-3xl border border-white/10 bg-[#141414] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
            {done ? (
              <div data-testid="contact-success" className="text-center py-16">
                <CheckCircle2 className="h-14 w-14 text-[#C9A227] mx-auto" />
                <h2 className="mt-4 font-display text-2xl font-bold text-[#F3EDDC]">Message sent!</h2>
                <p className="mt-2 text-[#B7AF98]">Our team will reach out within one business day.</p>
                <Button className="mt-6 rounded-full" variant="outline" onClick={() => setDone(false)}>Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="lead-form">
                <Field label="Name *"><Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" /></Field>
                <Field label="Business"><Input data-testid="lead-business" value={form.business} onChange={set("business")} placeholder="Smith & Co." /></Field>
                <Field label="Email *"><Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@business.com" /></Field>
                <Field label="Phone"><Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" /></Field>
                <Field label="Current website"><Input data-testid="lead-website" value={form.current_website} onChange={set("current_website")} placeholder="https://" /></Field>
                <Field label="Industry"><Input data-testid="lead-industry" value={form.industry} onChange={set("industry")} placeholder="Roofing, Restaurant…" /></Field>
                <Field label="What type of website do you need?">
                  <Select value={form.website_type} onValueChange={set("website_type")}>
                    <SelectTrigger data-testid="lead-website-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["New website", "Redesign", "Landing page", "E-commerce", "Not sure"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Budget">
                  <Select value={form.budget} onValueChange={set("budget")}>
                    <SelectTrigger data-testid="lead-budget"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["Under $500", "$500 - $1,000", "$1,000 - $1,500", "$1,500+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message"><Textarea data-testid="lead-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your project…" /></Field>
                </div>
                <div className="sm:col-span-2">
                  <Button data-testid="lead-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get my free quote"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </Container>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-[#D2CAB2]">{label}</Label>
      {children}
    </div>
  );
}
